import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import getParamValueFromUrl from '@Utils/GetParamValueFromUrl'
import Forwarder from '@SiteContainers/Forwarder'
import find from 'lodash/find'
import SettingsMain from '@SiteContainers/SettingsMain'
import AccountUserView from '@SiteContainers/AccountUserView'
import WorkspaceView from '@SiteContainers/WorkspaceView'
import Sidebar from '@SiteContainers/Sidebar'
import DeleteWorkspacePopup from '@SiteContainers/DeleteWorkspacePopup'
import CreateWorkspacePopup from '@SiteContainers/CreateWorkspacePopup'
import CreateAccountUserPopup from '@SiteContainers/CreateAccountUserPopup'
import ManageWorkspaceAccessPopup from '@SiteContainers/ManageWorkspaceAccessPopup'

import {
  fetchAccountUsers,
} from '@SiteModules/AccountUser/actions'

import {
  getAccountUsers,
  getHasLoadedInitially,
} from '@SiteModules/AccountUser/selectors'

import {
  getCurrentAccountId,
  getUserIsAccountAdmin,
} from '@SiteModules/Account/selectors'

import {
  getWorkspaces,
} from '@SiteModules/Workspace/selectors'

import {
  getDeleteWorkspacePopupIsOpen,
  getCreateWorkspacePopupIsOpen,
  getCreateAccountUserPopupIsOpen,
  getManageWorkspaceAccessPopupIsOpen,
} from '@SiteModules/Popup/selectors'

import './Settings.scss'

const Settings: React.FC = () => {
  const dispatch = useDispatch()
  const location = useLocation()

  const accountUsers = useSelector(getAccountUsers)
  const workspaces = useSelector(getWorkspaces)
  const hasLoadedInitially = useSelector(getHasLoadedInitially)
  const currentAccountId = useSelector(getCurrentAccountId)
  const userIsAccountAdmin = useSelector(getUserIsAccountAdmin)
  const deleteWorkspacePopupIsOpen = useSelector(getDeleteWorkspacePopupIsOpen)
  const createWorkspacePopupIsOpen =  useSelector(getCreateWorkspacePopupIsOpen)
  const createAccountUserPopupIsOpen =  useSelector(getCreateAccountUserPopupIsOpen)
  const manageWorkspaceAccessPopupIsOpen = useSelector(getManageWorkspaceAccessPopupIsOpen)

  const [activeSettings, setActiveSettings] = useState('profile')
  const [openedAccountUserId, setOpenedAccountUserId] = useState(-1)
  const [openedWorkspaceId, setOpenedWorkspaceId] = useState(-1)
  const [fullAccountUserView, setFullAccountUserView] = useState(false)
  const [fullWorkspaceView, setFullWorkspaceView] = useState(false)

  const shouldRenderAccountUserView = openedAccountUserId >= 0
  const shouldRenderWorkspaceView = openedWorkspaceId >= 0
  const showSettingsWithFullAccountUserView = shouldRenderAccountUserView && fullAccountUserView
  const showSettingsWithFullWorkspaceView = shouldRenderWorkspaceView && fullWorkspaceView

  useEffect(() => {
    const settingsFromUrl = getParamValueFromUrl(
      window.location.href,
      'settings'
    )

    if (settingsFromUrl) {
      setActiveSettings(settingsFromUrl)

      const url = new URL(window.location.href)
      url.searchParams.delete('settings')
      window.history.replaceState({}, '', url)
    }
  }, [location.search])

  useEffect(() => {
    if (currentAccountId >= 0 && !hasLoadedInitially) {
      resetOpenedAccountUser()
    }
  }, [currentAccountId, hasLoadedInitially])

  useEffect(() => {
    if (currentAccountId >= 0 && !hasLoadedInitially) {
      resetOpenedWorkspace()
    }
  }, [currentAccountId])

  useEffect(() => {
    if (currentAccountId > 0 && ['users', 'workspaces', 'statistics'].includes(activeSettings) && !hasLoadedInitially) {
      if (userIsAccountAdmin || activeSettings === 'statistics') {
        dispatch(fetchAccountUsers({ accountId: currentAccountId }))
      } else {
        setActiveSettings('profile')
      }
    }
  }, [activeSettings, currentAccountId])

  const resetOpenedAccountUser = () => {
    setOpenedAccountUserId(-1)
    setFullAccountUserView(false)
  }

  const resetOpenedWorkspace = () => {
    setOpenedWorkspaceId(-1)
    setFullWorkspaceView(false)
  }

  const renderSettings = () => {
    return (
      <div
        className="Settings"
        id="settings"
      >
        <Sidebar
          pageName=""
        />
        <SettingsMain
          currentAccountId={currentAccountId}
          activeSettings={activeSettings}
          userIsAccountAdmin={userIsAccountAdmin}
          showSettingsWithFullAccountUserView={showSettingsWithFullAccountUserView}
          showSettingsWithFullWorkspaceView={showSettingsWithFullWorkspaceView}
          setActiveSettings={setActiveSettings}
          setOpenedAccountUserId={setOpenedAccountUserId}
          setOpenedWorkspaceId={setOpenedWorkspaceId}
        />
        {renderAccountUserView()}
        {renderWorkspaceView()}
        {renderPopups()}
      </div>
    )
  }

  const renderAccountUserView = () => {
    const openedAccountUser = find(accountUsers, accountUser => accountUser.id === openedAccountUserId)

    return (
      <AccountUserView
        shouldRender={shouldRenderAccountUserView}
        accountUser={openedAccountUser}
        resetOpenedAccountUser={resetOpenedAccountUser}
        fullAccountUserView={fullAccountUserView}
        setFullAccountUserView={setFullAccountUserView}
        setOpenedAccountUserId={setOpenedAccountUserId}
      />
    )
  }

  const renderWorkspaceView = () => {
    const openedWorkspace = find(workspaces, workspace => workspace.id === openedWorkspaceId)

    return (
      <WorkspaceView
        shouldRender={shouldRenderWorkspaceView}
        workspace={openedWorkspace}
        resetOpenedWorkspace={resetOpenedWorkspace}
        fullWorkspaceView={fullWorkspaceView}
        setFullWorkspaceView={setFullWorkspaceView}
        setOpenedWorkspaceId={setOpenedWorkspaceId}
      />
    )
  }

  const renderPopups = () => {
    return (
      <React.Fragment>
        {deleteWorkspacePopupIsOpen && (
          <DeleteWorkspacePopup
            resetOpenedWorkspace={resetOpenedWorkspace}
          />
        )}
        {createWorkspacePopupIsOpen && (
          <CreateWorkspacePopup />
        )}
        {createAccountUserPopupIsOpen && (
          <CreateAccountUserPopup />
        )}
        {manageWorkspaceAccessPopupIsOpen && (
          <ManageWorkspaceAccessPopup />
        )}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Forwarder />
      {renderSettings()}
    </React.Fragment>
  )
}

export default Settings
